import { Popover } from "@base-ui/react/popover";
import { LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/admin/language-switcher";
import { ThemeSwitcher } from "@/components/admin/theme-switcher";
import { TimezoneSwitcher } from "@/components/admin/timezone-switcher";
import { Button } from "@/components/ui/button";
import { adminConfig } from "@/lib/config";
import type { AdminIdentity } from "@/lib/types";
import { cn } from "@/lib/utils";

function accountInitials(identity?: AdminIdentity) {
  const source = identity?.username?.trim() || identity?.actor_id?.trim() || "";
  const parts = source.replace(/[_-]+/g, " ").split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return source.slice(0, 1).toUpperCase() || "?";
}

function eventElement(event: Event) {
  const target = event.target;
  if (target instanceof Element) return target;
  return target instanceof Node ? target.parentElement : null;
}

function isSelectPopupEvent(event: Event) {
  return Boolean(eventElement(event)?.closest('[role="listbox"], [role="option"]'));
}

export function UserMenu({
  identity,
  loading,
  sessionError,
  sessionLabel,
  signingOut,
  sidebar = false,
  collapsed = false,
  onSignOut,
}: {
  identity?: AdminIdentity;
  loading: boolean;
  sessionError: boolean;
  sessionLabel: string;
  signingOut: boolean;
  sidebar?: boolean;
  collapsed?: boolean;
  onSignOut?: () => void;
}) {
  const { t } = useTranslation();
  const username = identity?.username ?? t("nav.localAccount", "Local Admin Account");
  const roles = identity?.roles.join(" / ") || (loading ? t("nav.loadingRoles", "Loading roles…") : t("nav.noRoles", "No roles"));

  return (
    <Popover.Root
      onOpenChange={(open, details) => {
        if (!open && isSelectPopupEvent(details.event)) details.cancel();
      }}
    >
      <Popover.Trigger
        className={cn(
          "relative text-muted transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus",
          sidebar
            ? collapsed
              ? "flex size-11 items-center justify-center rounded-full border border-line bg-panel text-xs font-normal text-ink hover:bg-panel-raised"
              : "flex min-h-11 w-full items-center gap-3 rounded-sm border border-transparent px-3 text-left text-sm hover:border-line hover:bg-panel hover:text-ink"
            : "flex size-8 items-center justify-center rounded-full border border-line bg-panel text-xs font-normal text-ink hover:bg-panel-raised pointer-coarse:size-11",
        )}
        aria-label={`${t("nav.accountMenu", "Account menu")}, ${sessionLabel}`}
      >
        <span
          className={cn(
            "relative flex shrink-0 items-center justify-center rounded-full border border-line bg-panel-raised text-xs font-normal text-ink",
            sidebar && collapsed ? "size-9" : "size-8",
          )}
        >
          <span aria-hidden="true">{accountInitials(identity)}</span>
          <span
            className={cn(
              "absolute right-0 top-0 size-2 rounded-full ring-2 ring-canvas",
              sessionError ? "bg-danger" : loading ? "bg-muted" : "bg-success",
            )}
            aria-hidden="true"
          />
        </span>
        {sidebar && !collapsed ? (
          <span className="min-w-0 text-left">
            <span className="block truncate text-xs font-normal text-ink">{username}</span>
            <span className="block truncate text-[11px] text-muted">{sessionLabel}</span>
          </span>
        ) : null}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner
          side={sidebar ? "right" : "bottom"}
          align="end"
          sideOffset={sidebar ? 12 : 8}
          className="z-50"
        >
          <Popover.Popup
            className={cn(
              "w-80 max-w-[calc(100vw-1.5rem)] rounded-sm border border-line bg-panel p-4 text-ink shadow-panel outline-hidden transition-all duration-150 data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 motion-reduce:transform-none motion-reduce:transition-none",
              sidebar ? "origin-left" : "origin-top-right",
            )}
          >
            <Popover.Title className="sr-only">{t("nav.accountMenu", "Account menu")}</Popover.Title>
            <div className="flex items-center gap-2.5">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-line bg-panel-raised text-xs font-normal text-ink" aria-hidden="true">
                {accountInitials(identity)}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-normal text-ink">{username}</p>
                <p className="truncate text-xs text-muted">{roles}</p>
              </div>
            </div>
            <p className="mt-2 text-[11px] leading-4 text-muted">{t("nav.enforcedHint", "Actions are enforced again by the Admin API.")}</p>

            <div className="mt-3 space-y-3 border-t border-line pt-3">
              <p className="font-mono text-[10px] font-normal uppercase tracking-[0.14em] text-muted">{t("nav.preferences", "Preferences")}</p>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-muted">{t("nav.theme", "Theme")}</span>
                <ThemeSwitcher />
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-muted">{t("nav.language", "Language")}</span>
                <LanguageSwitcher />
              </div>
              <div className="space-y-1.5">
                <span className="text-xs text-muted">{t("nav.timezone", "Timezone")}</span>
                <TimezoneSwitcher fullWidth />
              </div>
            </div>

            <div className="mt-3 border-t border-line pt-3">
              {onSignOut ? (
                <Button variant="ghost" className="w-full justify-start" onClick={onSignOut} disabled={signingOut} aria-label={signingOut ? t("auth.signingOut", "Signing out…") : t("auth.signOut", "Sign out")}>
                  <LogOut className="size-4" aria-hidden="true" />
                  {signingOut ? t("auth.signingOut", "Signing out…") : t("auth.signOut", "Sign out")}
                </Button>
              ) : null}
              <p className={cn("px-3 text-[11px] text-muted", onSignOut && "mt-2")}>
                {t("nav.version", "Version")} <span className="break-all font-mono">{adminConfig.appVersion}</span>
              </p>
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
